import { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import {
  Alert,
  Box,
  Button,
  Chip,
  LinearProgress,
  MenuItem,
  Pagination,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from '@mui/material';
import DownloadIcon from '@mui/icons-material/Download';
import CurrencyExchangeIcon from '@mui/icons-material/CurrencyExchange';
import {
  atualizarPrecosPedidoComercial,
  getPedidosComerciais,
} from '../../services/api';
import { getAuthPermissoes } from '../../services/authStorage';
import { useEstabelecimentoFiltroApi } from '../../context/EstabelecimentoContext';
import PageHeader from '../common/PageHeader';
import ResponsiveTable from '../common/ResponsiveTable';
import { getApiErrorMessage } from '../../utils/apiError';
import downloadCsv from '../../utils/downloadCsv';

const money = (value) =>
  Number(value || 0).toLocaleString('pt-BR', {
    style: 'currency',
    currency: 'BRL',
  });

const fmtDate = (value) => {
  if (!value) return '';
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return '';
  return d.toLocaleString('pt-BR');
};

const fmtDateOnly = (value) => {
  if (!value) return '';
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return '';
  return d.toLocaleDateString('pt-BR');
};

const STATUS_OPTS = [
  { value: '', label: 'Todos' },
  { value: 'Aberto', label: 'Abertos' },
  { value: 'Vencido', label: 'Vencidos' },
  { value: 'EmCaixa', label: 'No caixa' },
  { value: 'Finalizado', label: 'Baixados' },
  { value: 'Cancelado', label: 'Cancelados' },
  { value: 'Convertido', label: 'Convertidos' },
];

export default function PedidoComercialReport() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const estabelecimentoId = useEstabelecimentoFiltroApi();
  const permissions = useMemo(() => getAuthPermissoes() || {}, []);
  const can = Boolean(permissions.orcamento || permissions.relatorios);

  const tipoInicial =
    searchParams.get('tipo') === 'Pedido' ? 'Pedido' : searchParams.get('tipo') === 'Orcamento' ? 'Orcamento' : '';

  const [filters, setFilters] = useState({
    tipo: tipoInicial,
    status: '',
    termo: '',
    dataInicio: '',
    dataFim: '',
  });
  const [applied, setApplied] = useState({
    tipo: tipoInicial,
    status: '',
    termo: '',
    dataInicio: '',
    dataFim: '',
  });
  const [page, setPage] = useState(1);
  const [data, setData] = useState({ items: [], totalPages: 1, totalItems: 0 });
  const [loading, setLoading] = useState(false);
  const [busyId, setBusyId] = useState(null);
  const [error, setError] = useState('');
  const [info, setInfo] = useState('');

  useEffect(() => {
    const t =
      searchParams.get('tipo') === 'Pedido'
        ? 'Pedido'
        : searchParams.get('tipo') === 'Orcamento'
          ? 'Orcamento'
          : '';
    setFilters((p) => ({ ...p, tipo: t }));
    setApplied((p) => ({ ...p, tipo: t }));
    setPage(1);
  }, [searchParams]);

  const load = useCallback(async () => {
    if (!can) {
      setError('Sem permissão de orçamento/pedido ou relatórios.');
      return;
    }
    setLoading(true);
    setError('');
    try {
      const response = await getPedidosComerciais({
        ...applied,
        estabelecimentoId: estabelecimentoId || undefined,
        indice: page,
        qtde: 50,
      });
      const payload = response.data || {};
      const totalItems = payload.totalItems || 0;
      const pageSize = payload.pageSize || 50;
      setData({
        items: payload.items || [],
        totalItems,
        totalPages: Math.max(1, Math.ceil(totalItems / pageSize)),
      });
    } catch (e) {
      setError(getApiErrorMessage(e, 'Não foi possível carregar o relatório.'));
    } finally {
      setLoading(false);
    }
  }, [applied, page, can, estabelecimentoId]);

  useEffect(() => {
    load();
  }, [load]);

  const totalPagina = useMemo(
    () => (data.items || []).reduce((s, r) => s + Number(r.valorTotal || 0), 0),
    [data.items],
  );

  const aplicar = () => {
    setPage(1);
    setApplied({ ...filters });
  };

  const exportar = async () => {
    setLoading(true);
    setError('');
    try {
      const response = await getPedidosComerciais({
        ...applied,
        estabelecimentoId: estabelecimentoId || undefined,
        indice: 1,
        qtde: 500,
      });
      const items = response.data?.items || [];
      downloadCsv(
        `pedidos-comerciais_${applied.tipo || 'todos'}_${applied.status || 'todos'}.csv`,
        items.map((r) => ({
          Codigo: r.codigo,
          Tipo: r.tipo === 'Pedido' ? 'Pedido' : 'Orcamento',
          Status: r.status === 'Finalizado' ? 'Baixado' : r.status,
          Cliente: r.clienteNome || '',
          Documento: r.clienteDocumento || r.documento || '',
          Telefone: r.clienteTelefone || '',
          Cidade: r.clienteCidade || '',
          Pagamento: r.formaPagamentoNome || '',
          Criacao: fmtDate(r.dataCriacao),
          Validade: fmtDateOnly(r.validade),
          Total: Number(r.valorTotal || 0).toFixed(2).replace('.', ','),
          VendaId: r.vendaId || '',
        })),
      );
    } catch (e) {
      setError(getApiErrorMessage(e, 'Falha ao exportar CSV.'));
    } finally {
      setLoading(false);
    }
  };

  const atualizarPrecos = async (row) => {
    if (row.status !== 'Aberto') return;
    if (!window.confirm(`Atualizar preços de ${row.codigo} com a tabela atual?`)) return;
    setBusyId(row.id);
    setInfo('');
    try {
      const { data: r } = await atualizarPrecosPedidoComercial(row.id);
      setInfo(
        r.itensAlterados > 0
          ? `${row.codigo}: ${r.itensAlterados} item(ns). ${money(r.valorTotalAnterior)} → ${money(r.valorTotalNovo)}`
          : `${row.codigo}: sem diferença de preço.`,
      );
      await load();
    } catch (e) {
      setError(getApiErrorMessage(e, 'Falha ao atualizar preços.'));
    } finally {
      setBusyId(null);
    }
  };

  const abrirDoc = (row) => {
    const base = row.tipo === 'Pedido' ? '/pedidos-venda' : '/orcamentos';
    navigate(`${base}/${row.id}`);
  };

  return (
    <Box sx={{ p: { xs: 2, md: 3 } }}>
      <PageHeader
        title="Relatório — Orçamentos / Pedidos"
        subtitle="Vencidos, abertos, baixados e conversões. Atualize preços quando a tabela mudar."
        actions={
          <Button
            variant="outlined"
            startIcon={<DownloadIcon />}
            disabled={loading}
            onClick={exportar}
          >
            CSV
          </Button>
        }
      />

      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>
          {error}
        </Alert>
      )}
      {info && (
        <Alert severity="success" sx={{ mb: 2 }} onClose={() => setInfo('')}>
          {info}
        </Alert>
      )}

      <Paper variant="outlined" sx={{ p: 2, mb: 2 }}>
        <Stack direction={{ xs: 'column', md: 'row' }} spacing={1} flexWrap="wrap" useFlexGap>
          <TextField
            select
            label="Tipo"
            size="small"
            value={filters.tipo}
            onChange={(e) => {
              const tipo = e.target.value;
              const next = { ...filters, tipo };
              setFilters(next);
              setPage(1);
              setApplied(next);
            }}
            sx={{ minWidth: 160 }}
          >
            <MenuItem value="">Todos</MenuItem>
            <MenuItem value="Orcamento">Orçamentos</MenuItem>
            <MenuItem value="Pedido">Pedidos de venda</MenuItem>
          </TextField>
          <TextField
            select
            label="Status"
            size="small"
            value={filters.status}
            onChange={(e) => {
              const status = e.target.value;
              const next = { ...filters, status };
              setFilters(next);
              setPage(1);
              setApplied(next);
            }}
            sx={{ minWidth: 160 }}
          >
            {STATUS_OPTS.map((o) => (
              <MenuItem key={o.value || 'all'} value={o.value}>
                {o.label}
              </MenuItem>
            ))}
          </TextField>
          <TextField
            label="De"
            type="date"
            size="small"
            value={filters.dataInicio}
            onChange={(e) => setFilters((p) => ({ ...p, dataInicio: e.target.value }))}
            InputLabelProps={{ shrink: true }}
          />
          <TextField
            label="Até"
            type="date"
            size="small"
            value={filters.dataFim}
            onChange={(e) => setFilters((p) => ({ ...p, dataFim: e.target.value }))}
            InputLabelProps={{ shrink: true }}
          />
          <TextField
            label="Busca"
            size="small"
            value={filters.termo}
            onChange={(e) => setFilters((p) => ({ ...p, termo: e.target.value }))}
            onKeyDown={(e) => e.key === 'Enter' && aplicar()}
            sx={{ minWidth: 200 }}
          />
          <Button variant="contained" disabled={loading} onClick={aplicar}>
            Filtrar
          </Button>
        </Stack>
      </Paper>

      <Stack direction="row" spacing={2} sx={{ mb: 1 }} flexWrap="wrap" useFlexGap>
        <Typography variant="body2" color="text.secondary">
          {data.totalItems} documento(s)
        </Typography>
        <Typography variant="body2" fontWeight={700}>
          Total na página: {money(totalPagina)}
        </Typography>
      </Stack>

      {loading && <LinearProgress />}
      <ResponsiveTable>
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell>Código</TableCell>
              <TableCell>Tipo</TableCell>
              <TableCell>Cliente</TableCell>
              <TableCell>Criação</TableCell>
              <TableCell>Validade</TableCell>
              <TableCell>Status</TableCell>
              <TableCell align="right">Total</TableCell>
              <TableCell align="right">Ações</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {(data.items || []).map((row) => (
              <TableRow key={row.id} hover>
                <TableCell>
                  <Typography
                    fontWeight={800}
                    sx={{ cursor: 'pointer' }}
                    onClick={() => abrirDoc(row)}
                  >
                    {row.codigo}
                  </Typography>
                </TableCell>
                <TableCell>{row.tipo === 'Pedido' ? 'Pedido' : 'Orçamento'}</TableCell>
                <TableCell>
                  <Typography variant="body2" fontWeight={600}>
                    {row.clienteNome || 'Consumidor'}
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    {[row.clienteDocumento || row.documento, row.clienteCidade]
                      .filter(Boolean)
                      .join(' · ')}
                  </Typography>
                </TableCell>
                <TableCell>{fmtDate(row.dataCriacao)}</TableCell>
                <TableCell>{fmtDateOnly(row.validade) || '—'}</TableCell>
                <TableCell>
                  <Chip
                    size="small"
                    label={row.status === 'Finalizado' ? 'Baixado' : row.status}
                  />
                </TableCell>
                <TableCell align="right">{money(row.valorTotal)}</TableCell>
                <TableCell align="right">
                  <Button size="small" onClick={() => abrirDoc(row)}>
                    Abrir
                  </Button>
                  {row.status === 'Aberto' && (
                    <Button
                      size="small"
                      color="warning"
                      startIcon={<CurrencyExchangeIcon />}
                      disabled={busyId === row.id}
                      onClick={() => atualizarPrecos(row)}
                    >
                      Preços
                    </Button>
                  )}
                </TableCell>
              </TableRow>
            ))}
            {!loading && (data.items || []).length === 0 && (
              <TableRow>
                <TableCell colSpan={8}>
                  <Typography color="text.secondary" align="center" sx={{ py: 3 }}>
                    Nenhum documento neste filtro.
                  </Typography>
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </ResponsiveTable>

      {(data.totalPages || 1) > 1 && (
        <Stack alignItems="center" sx={{ mt: 2 }}>
          <Pagination
            page={page}
            count={data.totalPages || 1}
            onChange={(_, value) => setPage(value)}
          />
        </Stack>
      )}
    </Box>
  );
}
